import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import Avatar from '../components/common/Avatar';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const TeamPage = () => {
  const {
    API, activeTab, deleteTeamMember, disconnectTeamMemberCalendar, openEditTeamMember, products,
    setSelectedMemberForCalendar, setShowAddTeamMember, setShowCalendarModal, setTeamViewType,
    teamMemberAvatarUrls, teamMembers, teamViewType,
  } = useDashboard();

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white">Team Members</h3>
          <div className="flex items-center space-x-4">
            {/* View Toggle */}
            <div className="flex items-center bg-gray-600 rounded-lg p-1">
              <button
                onClick={() => setTeamViewType('grid')}
                className={`px-3 py-1 rounded text-sm transition-all ${
                  teamViewType === 'grid' 
                    ? 'bg-white text-gray-700 font-medium' 
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setTeamViewType('list')}
                className={`px-3 py-1 rounded text-sm transition-all ${
                  teamViewType === 'list' 
                    ? 'bg-white text-gray-700 font-medium' 
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                List
              </button>
            </div>
            <button
              onClick={() => setShowAddTeamMember(true)}
              className="clara-btn-primary px-4 py-2"
            >
              Add Team Member
            </button>
          </div>
        </div>
        
        {/* Grid View */}
        {teamViewType === 'grid' && (
          <div className="p-8 space-y-6">
            {teamMembers.map((member) => (
              <div key={member.id} className="p-6 border rounded-lg" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-4">
                    {/* Team Member Avatar */}
                    {member.avatar && teamMemberAvatarUrls[member.id] ? (
                      <img 
                        src={teamMemberAvatarUrls[member.id]}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-400 font-semibold text-lg">
                        {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <h4 className="clara-body font-semibold" style={{ color: 'var(--clara-primary)', margin: 0 }}>{member.name}</h4>
                      <p className="clara-body-sm">{member.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>{member.role}</p>
                        {member.invitation_status === 'accepted' || member.user_id ? (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-300">
                            Invited
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditTeamMember(member)}
                      className="clara-btn-primary px-3 py-1 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTeamMember(member.id)}
                      className="clara-btn-secondary px-3 py-1 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h5 className="clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Working Hours:</h5>
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {member.working_hours && Object.entries(member.working_hours)
                      .filter(([day]) => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(day))
                      .map(([day, hours]) => (
                      <div key={day} className="text-center">
                        <div className="font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>
                          {day.substring(0, 3)}
                        </div>
                        <div style={{ color: 'var(--clara-medium-gray)' }}>
                          {hours.enabled ? `${hours.start}-${hours.end}` : 'Off'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <h5 className="clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Assigned Services:</h5>
                  <div className="flex flex-wrap gap-2">
                    {member.assigned_products && member.assigned_products.length > 0 ? (
                      member.assigned_products.map((productId) => {
                        const product = products.find(p => p.id === productId);
                        return product ? (
                          <span key={productId} className="px-2 py-1 bg-gray-100 rounded text-xs" style={{ color: 'var(--clara-medium-gray)' }}>
                            {product.name}
                          </span>
                        ) : null;
                      })
                    ) : (
                      <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>No services assigned</span>
                    )}
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded" style={{ borderColor: 'var(--clara-border)' }}>
                  <div className="flex items-center justify-between">
                    <h5 className="clara-body-sm font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>Google Calendar:</h5>
                    <div className="flex items-center">
                      {member.calendar_integration && member.calendar_integration.is_active ? (
                        <span className="clara-body-sm" style={{ color: 'var(--clara-success)' }}>
                          Connected ✅ (Real Calendar API)
                        </span>
                      ) : (
                        <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                          Not Connected
                        </span>
                      )}
                      <span className="ml-2 text-xl">📅</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    {member.calendar_integration && member.calendar_integration.is_active ? (
                      <div className="flex space-x-2">
                        <span className="clara-body-sm text-green-600">
                          Connected: {new Date(member.calendar_integration.connected_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => disconnectTeamMemberCalendar(member.id)}
                          className="clara-btn-secondary px-3 py-1 text-sm"
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedMemberForCalendar(member);
                          setShowCalendarModal(true);
                        }}
                        className="clara-btn-primary px-3 py-1 text-sm"
                      >
                        Connect Real Calendar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {teamMembers.length === 0 && (
              <p className="clara-body-sm text-center py-8" style={{ color: 'var(--clara-medium-gray)' }}>No team members yet</p>
            )}
          </div>
        )}
        
        {/* List View */}
        {teamViewType === 'list' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Services</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Calendar</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {member.avatar && teamMemberAvatarUrls[member.id] ? (
                          <img 
                            src={teamMemberAvatarUrls[member.id]}
                            alt={member.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-400 font-semibold text-sm">
                            {member.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="clara-body font-medium" style={{ color: 'var(--clara-primary)', margin: 0 }}>{member.name}</p>
                          <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>{member.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="clara-body">{member.role}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.invitation_status === 'accepted' || member.user_id ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-300 rounded text-xs font-medium">
                          Invited
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {member.assigned_products && member.assigned_products.length > 0 ? (
                        <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                          {member.assigned_products.length} service{member.assigned_products.length > 1 ? 's' : ''}
                        </p>
                      ) : (
                        <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>None</p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.calendar_integration && member.calendar_integration.is_active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Connected
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                          Not Connected
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEditTeamMember(member)}
                        className="clara-btn-primary px-3 py-1 text-sm mr-2"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteTeamMember(member.id)}
                        className="clara-btn-secondary px-3 py-1 text-sm"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {teamMembers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center">
                      <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>No team members yet</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage;
